import React, { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Users,
  ExternalLink,
  Search,
  CalendarRange,
} from "lucide-react";
import {
  inputCls,
  MEETING_STATUSES,
  QUARTERS,
  fmtDate,
  statusBadgeClass,
} from "./shared";
import MeetingDialog from "./MeetingDialog";
import AttendanceDialog from "./AttendanceDialog";

export default function MeetingsTab({
  boardType,
  meetings,
  financialYears,
  refresh,
  writable,
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [attendanceFor, setAttendanceFor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Filters
  const [filterFY, setFilterFY] = useState("");
  const [filterQuarter, setFilterQuarter] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    return meetings.filter((m) => {
      if (m.board_type !== boardType) return false;
      if (filterFY && m.financial_year_id !== filterFY) return false;
      if (filterQuarter && String(m.quarter || "") !== String(filterQuarter)) return false;
      if (filterStatus && m.status !== filterStatus) return false;
      if (search) {
        const s = search.toLowerCase();
        const blob = `${m.venue || ""} ${m.agenda || ""} ${m.resolution || ""} ${m.notes || ""}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
  }, [meetings, boardType, filterFY, filterQuarter, filterStatus, search]);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setDialogOpen(true);
  };

  const doDelete = async () => {
    const m = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/meetings/${m.id}`);
      toast.success("Meeting removed");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const totalDocs = (m) => (m.documents || []).length;
  const presentCount = (m) =>
    (m.attendance || []).filter((a) => a.status === "Present").length;

  return (
    <div data-testid={`meetings-tab-${boardType}`}>
      {/* Filters */}
      <div className="bg-white border border-[#e5e0d8] rounded-sm p-4 mb-4 grid grid-cols-1 md:grid-cols-[1fr_180px_180px_180px_auto] gap-3">
        <div className="relative">
          <Search
            size={14}
            strokeWidth={1.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b655b]"
          />
          <input
            data-testid="meeting-search-input"
            className={inputCls + " pl-9"}
            placeholder="Search agenda, venue, resolution…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          data-testid="meeting-filter-fy"
          className={inputCls}
          value={filterFY}
          onChange={(e) => setFilterFY(e.target.value)}
        >
          <option value="">All Financial Years</option>
          {financialYears.map((f) => (
            <option key={f.id} value={f.id}>
              {f.label}
            </option>
          ))}
        </select>
        {boardType === "executive" ? (
          <select
            data-testid="meeting-filter-quarter"
            className={inputCls}
            value={filterQuarter}
            onChange={(e) => setFilterQuarter(e.target.value)}
          >
            <option value="">All Quarters</option>
            {QUARTERS.map((q) => (
              <option key={q.value} value={q.value}>
                {q.label}
              </option>
            ))}
          </select>
        ) : (
          <div />
        )}
        <select
          data-testid="meeting-filter-status"
          className={inputCls}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          {MEETING_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {writable && (
          <button
            onClick={openCreate}
            data-testid={`meeting-add-button-${boardType}`}
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm whitespace-nowrap"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Meeting
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center">
          <CalendarRange
            size={28}
            strokeWidth={1.5}
            className="mx-auto text-[#234333]"
          />
          <div className="mt-3 font-serif text-2xl text-[#1c1a17]">
            No meetings to show
          </div>
          <div className="mt-2 text-sm text-[#6b655b]">
            {meetings.filter((m) => m.board_type === boardType).length === 0
              ? `Add your first ${boardType === "executive" ? "Executive" : "General"} Board meeting.`
              : "Adjust the filters above to see matching meetings."}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid={`meetings-table-${boardType}`}>
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {[
                  "Date",
                  "FY",
                  ...(boardType === "executive" ? ["Quarter"] : []),
                  "Venue",
                  "Status",
                  "Docs",
                  "Attendance",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr
                  key={m.id}
                  data-testid={`meeting-row-${m.id}`}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                >
                  <td className="px-4 py-3 text-[#1c1a17] whitespace-nowrap">
                    {fmtDate(m.meeting_date)}
                    {m.meeting_time ? (
                      <div className="text-[11px] text-[#6b655b]">{m.meeting_time}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-[#6b655b]">{m.financial_year_label || "—"}</td>
                  {boardType === "executive" && (
                    <td className="px-4 py-3 text-[#6b655b]">Q{m.quarter || "—"}</td>
                  )}
                  <td className="px-4 py-3 text-[#6b655b] max-w-xs truncate">
                    {m.venue || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-block px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${statusBadgeClass(m.status)}`}
                    >
                      {m.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#6b655b] text-xs">{totalDocs(m)}</td>
                  <td className="px-4 py-3 text-xs">
                    <button
                      onClick={() => setAttendanceFor(m)}
                      data-testid={`meeting-attendance-${m.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Users size={12} strokeWidth={1.5} />
                      {presentCount(m)} present
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {m.documents && m.documents.length > 0 && (
                      <a
                        href={m.documents[0].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={m.documents[0].name || m.documents[0].category}
                        data-testid={`meeting-first-doc-${m.id}`}
                        className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm text-xs"
                      >
                        <ExternalLink size={12} strokeWidth={1.5} /> Open
                      </a>
                    )}
                    <button
                      onClick={() => openEdit(m)}
                      data-testid={`meeting-edit-${m.id}`}
                      className="ml-1 inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} />{" "}
                      {writable ? "Edit" : "View"}
                    </button>
                    {writable && (
                      <button
                        onClick={() => setConfirmDelete(m)}
                        data-testid={`meeting-delete-${m.id}`}
                        className="ml-1 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                      >
                        <Trash2 size={14} strokeWidth={1.5} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <MeetingDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        boardType={boardType}
        editing={editing}
        financialYears={financialYears}
        onSaved={refresh}
        writable={writable}
      />

      <AttendanceDialog
        open={!!attendanceFor}
        onClose={() => setAttendanceFor(null)}
        meeting={attendanceFor}
        onSaved={refresh}
        writable={writable}
      />

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Remove this meeting?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.financial_year_label}
              {confirmDelete?.quarter ? ` · Q${confirmDelete.quarter}` : ""} ·{" "}
              {fmtDate(confirmDelete?.meeting_date)} will be removed permanently along with any
              attached documents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="meeting-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
